import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material'
import { Tooltip } from '@mui/material'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useResponsiveTypography } from '../../../utility/responsiveTypography'
import {
  getFieldId,
  getKeyOfValue,
  getLegacySubfieldId,
  getLegacySubfieldReviewId,
} from '../../../pages/DataEntryPage/utils/utils'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  DROPDOWN_DEFAULT_VALUE,
  FIELD_TYPES,
  FORMATS,
  STANDARD_COLORS,
  TABLE_CONFIG_CONTEXTS,
  TABLE_CONTEXTS,
} from '../../../utility/constants'
import { GetApp as GetAppIcon, Visibility } from '@mui/icons-material'
import { fileServices } from '../../../services/fileServices'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { userConfig } from '../../../services/userConfigServices'
import TableSetting from '../../Dashboard/PreviewComponent/TableSettings/TableSetting'
import { formatUserFullName } from '../../../utility/userName'
import GroupView from '../../GroupView/GroupView'
import { getGroupById, getHiddenId } from '../../Dashboard/PreviewComponent/TableSettings/utils'
import {
  getDateAndDateTime,
  getFieldsForSettings,
  getTableRows,
  normalizeDataForGroupView,
} from '../util'
import VirtualizedTable from './VirtualizedTable'
import LongMenu from '../../../pages/DataEntryPage/Fields/LongMenu'
import StandaloneFilePreview from '../../Dashboard/PreviewComponent/components/StandaloneFilePreview'
import { useProcessContext } from '../../../store'
import { replacePlaceholders } from '../../../pages/DataEntryPage/utils/placeholderUtil.js'
import { extractDisplayName } from '../../../utility/fileUtil.js'
import FieldHistory from '../../DataEntry/FieldHistory.js'
import { formatCurrency } from '../../../pages/DataEntryPage/utils/currency.js'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'

const TableFieldView = ({
  activityType,
  field,
  value,
  reviews,
  comments,
  subfields = [],
  tableSequenceAndHeaders = [],
  listData,
  dynamicListData,
  data,
  previewCurrency,
  processId,
  isPreview = false,
  defaultCurrency,
  externalValues = {},
  fieldMappings = {},
  fieldMetadata = {},
}) => {
  const { user } = useAuthContext()
  const { tablePreviewHeaderFont } = useResponsiveTypography()
  const [fetchedConfig, setFetchedConfig] = useState(false)
  const [groupByColumns, setGroupByColumns] = useState(null)
  const [hiddenColumns, setHiddenColumns] = useState(null)
  const [accumulatedColumn, setAccumulatedColumn] = useState(null)
  const [normalizedDataForGroupView, setNormalizedDataForGroupView] = useState(null)
  const { columnsForGroupBy, columnsForSetting, selectTotalArr } = getFieldsForSettings(subfields)
  const [filePreview, setFilePreview] = useState(null)
  const [rows, setRows] = useState([])
  const [freezeColumnId, setFreezeColumnId] = useState(null)
  const fixedSerialNo = useRef(null)
  const fixedSerialNoValue = useRef(null)

  const processContext = useProcessContext((state) => state.processContext)

  // Build O(1) lookup maps once from the listData array
  const listDataIdMap = useMemo(
    () =>
      (listData || []).reduce((acc, it) => {
        acc[it.id] = it
        return acc
      }, {}),
    [listData],
  )

  const listIdsWithPlaceholders = useMemo(() => {
    const result = new Set()
    const seen = new Set()
    for (const it of listData || []) {
      if (it.list_id == null || seen.has(it.list_id)) continue
      seen.add(it.list_id)
      if (Array.isArray(it.placeholders) && it.placeholders.length > 0) {
        result.add(it.list_id)
      }
    }
    return result
  }, [listData])

  const subfieldsKey = useMemo(() => {
    return subfields?.map((sf) => sf.id).join(',')
  }, [subfields])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (field.is_selectable_table) {
      setRows(data?.selectable_tables_data[`st_${field.id}`] || [])
    } else {
      setRows(getTableRows(field, value, subfields))
    }
  }, [subfieldsKey])

  // useEffect(() => {
  //   if (field.is_selectable_table) {
  //     setRows(data?.selectable_tables_data?.[`st_${field.id}`] || [])
  //   } else {
  //     setRows(getTableRows(field, value, subfields))
  //   }
  // }, [field, value, subfields, data])

  const getFilteredTableSequenceAndHeaders = (tableSequenceAndHeaders) => {
    if (field.is_selectable_table) {
      const selectedRows = data?.selectable_tables_data[`st_${field.id}`] || []

      // First filter: select rows that match the condition
      let filteredAllRowsWithHeaders = tableSequenceAndHeaders.filter(
        (it) => it.row == null || selectedRows.includes(it.row + ''),
      )
      // Second filter: remove headers without children
      filteredAllRowsWithHeaders = filteredAllRowsWithHeaders.filter(
        (it, i) =>
          !isNaN(it.row) || (it.row == null && !isNaN(filteredAllRowsWithHeaders[i + 1]?.row)),
      )
      return filteredAllRowsWithHeaders
    } else {
      return tableSequenceAndHeaders
    }
  }

  const filteredTableSequenceAndHeaders = useMemo(() => {
    return getFilteredTableSequenceAndHeaders(tableSequenceAndHeaders)
  }, [tableSequenceAndHeaders, data?.selectable_tables_data, field.id, field.is_selectable_table])

  useEffect(() => {
    ;(async () => {
      groupByColumns &&
        fetchedConfig &&
        (await saveUserTableConfigs(
          user,
          TABLE_CONFIG_CONTEXTS.GROUP_DATA,
          groupByColumns,
          groupByColumns && groupByColumns.length <= 0 ? '' : (accumulatedColumn || []).join(','),
        ))
      if (groupByColumns != null) groupByColumns.length <= 0 && setAccumulatedColumn([])
    })()
  }, [groupByColumns])

  useEffect(() => {
    ;(async () => {
      hiddenColumns &&
        fetchedConfig &&
        (await saveUserTableConfigs(user, TABLE_CONFIG_CONTEXTS.HIDDEN_COLUMN, hiddenColumns, null))
    })()
  }, [hiddenColumns])

  useEffect(() => {
    ;(async () => {
      accumulatedColumn &&
        fetchedConfig &&
        (await saveUserTableConfigs(
          user,
          TABLE_CONFIG_CONTEXTS.GROUP_DATA,
          groupByColumns,
          (accumulatedColumn || []).join(','),
        ))
    })()
  }, [accumulatedColumn])

  useEffect(() => {
    if (user && fetchedConfig && freezeColumnId) {
      ;(async () => {
        await saveUserTableConfigs(
          user,
          TABLE_CONFIG_CONTEXTS.VERTICAL_FREEZE,
          freezeColumnId,
          null,
        )
      })()
    }
  }, [freezeColumnId])

  const handleFreeze = (path) => {
    const numericPath = Array.isArray(path) ? path.map((item) => Number(item)) : [Number(path)]
    setFreezeColumnId(numericPath)
  }

  useEffect(() => {
    ;(async () => {
      await getUserTableConfigs(user)
    })()
  }, [])

  /* update when there is any changes in columns for respective different views  or accumulated fields*/
  const saveUserTableConfigs = async (
    user,
    tableConfigContext,
    columns = [],
    accumulatedColumn,
  ) => {
    try {
      return await userConfig.saveTableConfig(
        user,
        field.id,
        columns,
        TABLE_CONTEXTS.DATA_PREVIEW_VIEW,
        activityType,
        tableConfigContext,
        accumulatedColumn,
      )
    } catch (error) {
      console.error(error)
    }
  }

  const getUserTableConfigs = async (user) => {
    try {
      const response = await userConfig.getTableConfig(user)
      /* set group by ids */
      const groupedColumns = getGroupById(
        response.data,
        TABLE_CONTEXTS.DATA_PREVIEW_VIEW,
        TABLE_CONFIG_CONTEXTS.GROUP_DATA,
        field.id,
        activityType,
      )
      const hiddenColumns = getHiddenId(
        response.data,
        TABLE_CONTEXTS.DATA_PREVIEW_VIEW,
        TABLE_CONFIG_CONTEXTS.HIDDEN_COLUMN,
        field.id,
        activityType,
      )
      setGroupByColumns(groupedColumns.columns)
      setAccumulatedColumn(
        groupedColumns.accumulatedColumn ? groupedColumns.accumulatedColumn.split(',') : [],
      )
      setHiddenColumns(hiddenColumns)

      /* set freeze ids */
      const freezeColumnsRemote = getHiddenId(
        response.data,
        TABLE_CONTEXTS.DATA_PREVIEW_VIEW,
        TABLE_CONFIG_CONTEXTS.VERTICAL_FREEZE,
        field.id,
        ACTIVITY_TYPES.DATA_ENTRY,
      )

      setFreezeColumnId(freezeColumnsRemote)
      setFetchedConfig(true)
      return response.data
    } catch (error) {
      console.error(error)
    }
  }

  const downloadFile = async (value) => {
    await fileServices.downloadFile(user, value)
  }

  useEffect(() => {
    setNormalizedDataForGroupView(
      normalizeDataForGroupView(
        rows,
        subfields,
        listData,
        dynamicListData,
        data.data,
        externalValues,
        fieldMappings,
        listDataIdMap,
        fieldMetadata,
      ),
    )
  }, [
    subfields,
    rows,
    externalValues,
    fieldMappings,
    fieldMetadata,
    data.data,
    listDataIdMap,
    listData,
    dynamicListData,
  ])

  const renderValue = (row, sf, value, _value) => {
    switch (sf.data_structure) {
      case FIELD_TYPES.CURRENCY:
        return formatCurrency(
          value[getLegacySubfieldId(sf.id, row)],
          getKeyOfValue(CURRENCY_COUNTRY_CODE, previewCurrency[sf.id]),
          { defaultCurrency },
        )
      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        return getDateAndDateTime(value[getLegacySubfieldId(sf.id, row)], sf.data_structure)
      case FIELD_TYPES.FILE:
        return extractDisplayName(_value)
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
      case FIELD_TYPES.TIMESTAMPED_DATE:
      case FIELD_TYPES.TIMESTAMPED_DATETIME: {
        const timestampedValue = _value
        if (!timestampedValue || !Array.isArray(timestampedValue)) return ''
        const formattedValue = timestampedValue.map((v, index) => {
          const user = dynamicListData?.users?.find((u) => u.id === v.user_id)
          const username = user ? formatUserFullName(user) : null
          // Split text by newline and render each part on a new line
          const lines = v.text.split('\n').map((line, i) => (
            <span key={`${index}-${i}`}>
              {line}
              <br />
            </span>
          ))
          return (
            <div key={index}>
              <i>{v.timestamp}:</i> {lines} {username ? ` - ${username}` : ''}
            </div>
          )
        })
        return <div>{formattedValue}</div>
      }
      default:
        return _value
    }
  }

  const renderSerialNo = (index) => {
    return (
      <TableCell
        ref={fixedSerialNoValue}
        sx={{
          minWidth: 10,
          position: 'sticky',
          left: 0,
          zIndex: 3,
          backgroundColor: 'white',
          top: 0,
        }}
      >
        {index + 1}
      </TableCell>
    )
  }

  const freezeIndex = subfields
    ?.filter((sf) => !hiddenColumns?.includes(sf.id))
    .findIndex((sf) => freezeColumnId?.includes(sf.id))
  return (
    <>
      <TableContainer
        id={getFieldId(field.id)}
        style={{ margin: '16px 0px', width: '100%', maxWidth: '100%', overflowX: 'auto' }}
      >
        {/* table settings */}
        <Toolbar
          style={{
            textAlign: 'center',
            minHeight: '4px',
            marginBottom: 8,
            padding: '0px 8px',
          }}
        >
          <Typography variant="h6" style={{ fontSize: tablePreviewHeaderFont }}>
            {field.field_template_name}
          </Typography>
          <TableSetting
            columns={columnsForSetting}
            columnsForGroupBy={columnsForGroupBy}
            selectTotalArr={selectTotalArr}
            groupByColumns={groupByColumns}
            setGroupByColumns={setGroupByColumns}
            hiddenColumns={hiddenColumns || []}
            setHiddenColumns={setHiddenColumns}
            accumulatedColumn={accumulatedColumn}
            setAccumulatedColumn={setAccumulatedColumn}
            handleFreeze={handleFreeze}
            freezeColumnId={freezeColumnId}
          />
        </Toolbar>
        {groupByColumns?.length > 0 ? (
          /* grouped table view */
          <GroupView
            data={subfields}
            normalizedDataForGroupView={normalizedDataForGroupView}
            keyValuePairs={data.data}
            groupingFields={groupByColumns.map((it) => 'f_' + it)}
            accumulatedFields={accumulatedColumn.map((it) => 'f_' + it)}
            hiddenFields={hiddenColumns.map((it) => 'f_' + it)}
            reviewData={data.review_data}
            previewCurrency={previewCurrency}
            defaultCurrency={defaultCurrency}
          />
        ) : (
          <>
            {filteredTableSequenceAndHeaders.length > 15 ? (
              <VirtualizedTable
                activityType={activityType}
                rows={rows}
                tableSequenceAndHeaders={filteredTableSequenceAndHeaders}
                subfields={subfields}
                values={value}
                reviews={reviews}
                comments={comments}
                hiddenColumns={hiddenColumns}
                listData={listData}
                dynamicListData={dynamicListData}
                previewCurrency={previewCurrency}
                freezeColumnId={freezeColumnId}
                data={data}
                processId={processId}
                externalValues={externalValues}
                fieldMappings={fieldMappings}
                fieldMetadata={fieldMetadata}
              />
            ) : (
              <div className="preview-inside-table" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table
                  className="responsiveTable "
                  stickyHeader
                  style={{ tableLayout: 'auto', width: '100%', minWidth: '1000px' }}
                >
                  <TableHead>
                    <TableRow>
                      {tableSequenceAndHeaders.length > 0 && (
                        <TableCell
                          ref={fixedSerialNo}
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            backgroundColor: 'white',
                            minWidth: 46,
                            top: 0,
                          }}
                        >
                          Sl. No.
                        </TableCell>
                      )}

                      {subfields
                        ?.filter((sf) => !hiddenColumns?.includes(sf.id))
                        ?.map((sf, index) => {
                          const isFrozen = index <= freezeIndex

                          const columnWidth = sf.subfield_column_width_in_rem * 18
                          return (
                            <TableCell
                              sx={{
                                minWidth: columnWidth,
                                position: isFrozen ? 'sticky' : 'static',
                                left: isFrozen ? `0` : 'auto',
                                zIndex: isFrozen ? 2 : 'auto',
                                backgroundColor: isFrozen ? 'lightgrey' : 'white',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {sf.field_template_name}
                            </TableCell>
                          )
                        })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTableSequenceAndHeaders?.map(({ row, is_header, header_value }, i) => {
                      return !is_header ? (
                        <TableRow key={row}>
                          {renderSerialNo(i)}
                          {subfields
                            ?.filter((sf) => !hiddenColumns?.includes(sf.id))
                            ?.map((sf, index) => {
                              const _value = value[getLegacySubfieldId(sf.id, row)]
                              const _review = reviews[getLegacySubfieldReviewId(sf.id, row)]

                              const isFrozen = index <= freezeIndex

                              // Handling Timestamped BigText
                              if (
                                [
                                  FIELD_TYPES.TIMESTAMPED_BIGTEXT,
                                  FIELD_TYPES.TIMESTAMPED_DATE,
                                  FIELD_TYPES.TIMESTAMPED_DATETIME,
                                ].includes(sf.data_structure)
                              ) {
                                const formattedValue = Array.isArray(_value)
                                  ? _value.map((v, index) => {
                                      if (!v || typeof v !== 'object' || !v.text) return null

                                      const lines = v.text.split('\n').map((line, i) => (
                                        <span key={`${index}-${i}`}>
                                          {line}
                                          <br />
                                        </span>
                                      ))
                                      const user = dynamicListData?.users?.find(
                                        (u) => u.id === v.user_id,
                                      )
                                      const username = user ? formatUserFullName(user) : null
                                      // Display timestamp and user_id if available

                                      const tooltipContent = `${v.timestamp}:${lines}${
                                        username ? ` - ${username}` : ''
                                      }`
                                      const textColor =
                                        activityType === ACTIVITY_TYPES.REVIEW
                                          ? _review === undefined
                                            ? null
                                            : _review
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : activityType === ACTIVITY_TYPES.LONG_RUNNING
                                                ? _review === true
                                                : STANDARD_COLORS.PREVIEW_REJECTED
                                          : null

                                      return (
                                        <div key={index} style={{ fontSize: '12px' }}>
                                          <Tooltip title={tooltipContent}>
                                            <span style={{ color: textColor }}>
                                              <i>{v.timestamp}</i> {lines}{' '}
                                              {username ? ` - ${username}` : ''}
                                            </span>
                                          </Tooltip>
                                        </div>
                                      )
                                    })
                                  : null

                                return (
                                  <TableCell
                                    key={sf.id + '-' + row}
                                    id={sf.id + '-' + row}
                                    style={{
                                      minWidth: sf.subfield_column_width_in_rem * 16,
                                      padding: 4,
                                      position: isFrozen ? 'sticky' : 'static',
                                      left: isFrozen ? `0` : 'auto',
                                      zIndex: isFrozen ? 1 : 'auto',
                                      backgroundColor: isFrozen ? 'lightgrey' : 'white',
                                      fontWeight: '500',
                                      color:
                                        activityType === ACTIVITY_TYPES.REVIEW
                                          ? _review === undefined
                                            ? null
                                            : _review
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                          : activityType === ACTIVITY_TYPES.LONG_RUNNING
                                            ? _review === true
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                            : null,
                                      // whiteSpace: 'normal',
                                      // wordWrap: 'break-word',
                                      // overflowWrap: 'break-word',
                                      alignItems: 'start',
                                      width: '100%',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <FieldHistory
                                      fieldId={`${sf.id}__${row}`}
                                      rowId={row}
                                      field={sf}
                                      listDataIdMap={listDataIdMap}
                                      listIdsWithPlaceholders={listIdsWithPlaceholders}
                                      dynamicListData={dynamicListData}
                                      processId={processId}
                                      allValues={data.data}
                                      isPreview={isPreview}
                                      externalValues={externalValues}
                                      fieldMappings={fieldMappings}
                                    >
                                      {formattedValue}
                                    </FieldHistory>
                                  </TableCell>
                                )
                              } else if (sf.data_structure === FIELD_TYPES.FILE) {
                                return (
                                  <TableCell
                                    key={sf.id + '-' + row}
                                    id={sf.id + '-' + row}
                                    style={{
                                      minWidth: sf.subfield_column_width_in_rem * 16,
                                      padding: 4,
                                      position: isFrozen ? 'sticky' : 'static',
                                      left: isFrozen ? `0` : 'auto',
                                      zIndex: isFrozen ? 1 : 'auto',
                                      backgroundColor: isFrozen ? 'lightgrey' : 'white',
                                      fontWeight: '500',
                                      color:
                                        activityType === ACTIVITY_TYPES.REVIEW
                                          ? _review === undefined
                                            ? null
                                            : _review
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                          : activityType === ACTIVITY_TYPES.LONG_RUNNING
                                            ? _review === true
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                            : null,
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    <div
                                      style={{
                                        alignItems: 'start',
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <FieldHistory
                                        width="auto"
                                        fieldId={`${sf.id}__${row}`}
                                        rowId={row}
                                        field={sf}
                                        listDataIdMap={listDataIdMap}
                                        listIdsWithPlaceholders={listIdsWithPlaceholders}
                                        dynamicListData={dynamicListData}
                                        processId={processId}
                                        allValues={data.data}
                                        isPreview={isPreview}
                                        externalValues={externalValues}
                                        fieldMappings={fieldMappings}
                                      >
                                        {extractDisplayName(_value)}
                                      </FieldHistory>
                                      {_value && (
                                        <LongMenu
                                          options={[
                                            {
                                              label: 'Preview',
                                              icon: (
                                                <IconButton
                                                  size="small"
                                                  sx={{ padding: '0px 4px' }}
                                                >
                                                  <RemoveRedEyeOutlinedIcon fontSize="18px" />
                                                </IconButton>
                                              ),
                                              onClick: () => setFilePreview(_value),
                                            },
                                            {
                                              label: 'Download',
                                              icon: (
                                                <IconButton
                                                  size="small"
                                                  sx={{ padding: '0px 4px' }}
                                                >
                                                  <FileDownloadOutlinedIcon fontSize="18px" />
                                                </IconButton>
                                              ),
                                              onClick: () => downloadFile(_value),
                                            },
                                          ]}
                                        />
                                      )}
                                    </div>
                                  </TableCell>
                                )
                              } else if (sf.data_structure === FIELD_TYPES.LIST) {
                                let actualValue = null

                                if (
                                  _value == null ||
                                  _value === '' ||
                                  _value == DROPDOWN_DEFAULT_VALUE
                                ) {
                                  actualValue = ''
                                } else if (!sf.list_id) {
                                  const type = sf?.dynamic_field_type?.type
                                  const items = dynamicListData[type]
                                  const item = items?.find((ld) => ld.id == _value)

                                  if (type === 'users') {
                                    actualValue = formatUserFullName(item)
                                  } else {
                                    actualValue = item?.name
                                  }
                                } else {
                                  let listDataValue = listData?.find(
                                    (ld) => ld.id === _value,
                                  )?.value

                                  if (
                                    typeof listDataValue === 'string' &&
                                    listDataValue.includes('[[')
                                  ) {
                                    actualValue = replacePlaceholders(
                                      listDataValue,
                                      { ...data.data, ...externalValues, ...value },
                                      listDataIdMap,
                                      fieldMappings[sf.id] || sf.metadata?.placeholder_map || {},
                                      `__${row}`,
                                      fieldMetadata,
                                    )
                                  } else {
                                    actualValue = listDataValue ?? ''
                                  }
                                }

                                return (
                                  <TableCell
                                    key={sf.id + '-' + row}
                                    id={sf.id + '-' + row}
                                    style={{
                                      minWidth: sf.subfield_column_width_in_rem * 16,
                                      padding: 4,
                                      position: isFrozen ? 'sticky' : 'static',
                                      left: isFrozen ? `0` : 'auto',
                                      zIndex: isFrozen ? 1 : 'auto',
                                      backgroundColor: isFrozen ? 'lightgrey' : 'white',
                                      fontWeight: '500',
                                      color:
                                        activityType === ACTIVITY_TYPES.REVIEW
                                          ? _review === undefined
                                            ? null
                                            : _review
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                          : activityType === ACTIVITY_TYPES.LONG_RUNNING
                                            ? _review === true
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                            : null,
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    <FieldHistory
                                      fieldId={`${sf.id}__${row}`}
                                      rowId={row}
                                      field={sf}
                                      listDataIdMap={listDataIdMap}
                                      listIdsWithPlaceholders={listIdsWithPlaceholders}
                                      dynamicListData={dynamicListData}
                                      processId={processId}
                                      allValues={data.data}
                                      isPreview={isPreview}
                                      rowSuffix={`__${row}`}
                                      externalValues={externalValues}
                                      fieldMappings={fieldMappings}
                                    >
                                      {actualValue}
                                    </FieldHistory>
                                  </TableCell>
                                )
                              } else if (sf.data_structure === FIELD_TYPES.LIST_MULTI) {
                                let actualValue = null

                                if (!sf.list_id) {
                                  const type = sf?.dynamic_field_type?.type
                                  const items = dynamicListData[type]

                                  if (Array.isArray(_value) && items?.length) {
                                    if (type === 'users') {
                                      actualValue = _value
                                        .map((id) => {
                                          if (
                                            id == null ||
                                            id === '' ||
                                            id == DROPDOWN_DEFAULT_VALUE
                                          ) {
                                            return ''
                                          }
                                          const item = items.find((ld) => ld.id == id)
                                          return item ? formatUserFullName(item) : id
                                        })
                                        .filter(Boolean)
                                    } else {
                                      actualValue = _value
                                        .map((id) => {
                                          if (
                                            id == null ||
                                            id === '' ||
                                            id == DROPDOWN_DEFAULT_VALUE
                                          ) {
                                            return ''
                                          }
                                          const item = items.find((ld) => ld.id == id)
                                          return item ? item?.name : id
                                        })
                                        .filter(Boolean)
                                    }
                                  }
                                } else {
                                  if (Array.isArray(_value)) {
                                    const listValues = _value
                                      .map((id) => {
                                        if (
                                          id == null ||
                                          id === '' ||
                                          id == DROPDOWN_DEFAULT_VALUE
                                        ) {
                                          return ''
                                        }
                                        const listDataValue = listData?.find(
                                          (ld) => ld.id === id,
                                        )?.value
                                        if (
                                          typeof listDataValue === 'string' &&
                                          listDataValue.includes('[[')
                                        ) {
                                          return replacePlaceholders(
                                            listDataValue,
                                            { ...data.data, ...externalValues, ...value },
                                            listDataIdMap,
                                            fieldMappings[sf.id] ||
                                              sf.metadata?.placeholder_map ||
                                              {},
                                            `__${row}`,
                                            fieldMetadata,
                                          )
                                        }
                                        return listDataValue ?? ''
                                      })
                                      .filter(Boolean)
                                    actualValue = listValues
                                  }
                                }

                                // Render each value on its own line
                                const displayValue = Array.isArray(actualValue)
                                  ? actualValue.map((val, i) => (
                                      <>
                                        <span>{val}</span>
                                        <br />
                                      </>
                                    ))
                                  : actualValue || '--'
                                return (
                                  <TableCell
                                    key={sf.id + '-' + row}
                                    id={sf.id + '-' + row}
                                    style={{
                                      minWidth: sf.subfield_column_width_in_rem * 16,
                                      padding: 4,
                                      position: isFrozen ? 'sticky' : 'static',
                                      left: isFrozen ? `0` : 'auto',
                                      zIndex: isFrozen ? 1 : 'auto',
                                      backgroundColor: isFrozen ? 'lightgrey' : 'white',
                                      fontWeight: '500',
                                      color:
                                        activityType === ACTIVITY_TYPES.REVIEW
                                          ? _review === undefined
                                            ? null
                                            : _review
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                          : activityType === ACTIVITY_TYPES.LONG_RUNNING
                                            ? _review === true
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : STANDARD_COLORS.PREVIEW_REJECTED
                                            : null,
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    <FieldHistory
                                      fieldId={`${sf.id}__${row}`}
                                      rowId={row}
                                      field={sf}
                                      listDataIdMap={listDataIdMap}
                                      listIdsWithPlaceholders={listIdsWithPlaceholders}
                                      dynamicListData={dynamicListData}
                                      processId={processId}
                                      allValues={data.data}
                                      isPreview={isPreview}
                                      rowSuffix={`__${row}`}
                                      externalValues={externalValues}
                                      fieldMappings={fieldMappings}
                                    >
                                      {displayValue}
                                    </FieldHistory>
                                  </TableCell>
                                )
                              } else {
                                return (
                                  <TableCell
                                    key={sf.id + '-' + row}
                                    id={sf.id + '-' + row}
                                    style={{
                                      minWidth: sf.subfield_column_width_in_rem * 16,
                                      padding: 4,
                                      position: isFrozen ? 'sticky' : 'static',
                                      left: isFrozen ? `0` : 'auto',
                                      zIndex: isFrozen ? 1 : 'auto',
                                      backgroundColor: isFrozen ? 'lightgrey' : 'white',
                                      color:
                                        activityType === ACTIVITY_TYPES.REVIEW
                                          ? _review === undefined
                                            ? null
                                            : _review
                                              ? STANDARD_COLORS.PREVIEW_ACCEPTED
                                              : activityType === ACTIVITY_TYPES.LONG_RUNNING
                                                ? _review === true
                                                : STANDARD_COLORS.PREVIEW_REJECTED
                                          : null,
                                      whiteSpace: 'normal',
                                      wordWrap: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    <FieldHistory
                                      fieldId={`${sf.id}__${row}`}
                                      rowId={row}
                                      field={sf}
                                      listDataIdMap={listDataIdMap}
                                      listIdsWithPlaceholders={listIdsWithPlaceholders}
                                      dynamicListData={dynamicListData}
                                      processId={processId}
                                      allValues={data.data}
                                      isPreview={isPreview}
                                      externalValues={externalValues}
                                      fieldMappings={fieldMappings}
                                    >
                                      <div
                                        style={{
                                          display: 'block',
                                          alignItems: 'center',
                                          textAlign: [
                                            FIELD_TYPES.CURRENCY,
                                            FIELD_TYPES.NUMBER,
                                            FIELD_TYPES.DATE,
                                            FIELD_TYPES.DATETIME,
                                          ].includes(sf.data_structure)
                                            ? 'right'
                                            : 'left',
                                          justifyContent: [
                                            FIELD_TYPES.CURRENCY,
                                            FIELD_TYPES.NUMBER,
                                            FIELD_TYPES.DATE,
                                            FIELD_TYPES.DATETIME,
                                          ].includes(sf.data_structure)
                                            ? 'end'
                                            : 'start',
                                          wordWrap: 'break-word',
                                          overflowWrap: 'break-word',
                                          whiteSpace: 'normal',
                                          width: '100%',
                                        }}
                                      >
                                        {renderValue(row, sf, value, _value)}
                                      </div>
                                    </FieldHistory>
                                  </TableCell>
                                )
                              }
                            })}
                        </TableRow>
                      ) : (
                        <TableRow key={'header' + i} style={{ backgroundColor: '#f0f0f0' }}>
                          {renderSerialNo(i)}
                          <TableCell
                            style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}
                            colSpan={subfields.length}
                          >
                            <Box minHeight={'27px'} display={'flex'} alignItems={'center'}>
                              <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
                                {header_value}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </TableContainer>
      <StandaloneFilePreview
        fileKey={filePreview}
        open={filePreview != null}
        onClose={() => setFilePreview(null)}
      />
    </>
  )
}

export default TableFieldView
